// redux/studyCaseSlice.ts
import { createSlice } from "@reduxjs/toolkit";

interface StudyTabData {
  [tabName: string]: {
    data: any;
    loading: boolean;
    error: string | null;
  };
}

const initialState: StudyTabData = {
  currentStudyTabData: {
    data: null,
    loading: false,
    error: null,
  },
  company_products: {
    data: [],
    loading: false,
    error: null,
  },
  product_search: {
    data: [],
    loading: false,
    error: null,
  },
  config_profile_names: {
    data: [],
    loading: false,
    error: null,
  },
  config_message_types: {
    data: [],
    loading: false,
    error: null,
  },
  config_forms: {
    data: [],
    loading: false,
    error: null,
  },
  regulatory_reports: {
    data: { reports: [], total_reports: 0 },
    loading: false,
    error: null,
  },
};

const caseSlice = createSlice({
  name: "case",
  initialState,
  reducers: {
    removeSubstanceById: (state, action) => {
      state.substance_information = {
        data: state.substance_information?.data?.filter(
          (item: any) => item.id !== action.payload
        ),
        loading: false,
        error: null,
      };
    },
    setCurrentStudyTabData: (state, action) => {
      state.currentStudyTabData = {
        data: action.payload,
        loading: false,
        error: null,
      }
    },
    removeProductPartById: (state, action) => {
      state.product_part_information = {
        data: state.product_part_information?.data?.filter(
          (item: any) => item.id !== action.payload
        ),
        loading: false,
        error: null,
      };
    },
    removeProductIndicationById: (state, action) => {
      state.product_indication_information = {
        data: state.product_indication_information?.data?.filter(
          (item: any) => item.id !== action.payload
        ),
        loading: false,
        error: null,
      };
    },
    removeQualityControlById: (state, action) => {
      state.product_quality_control_information = {
        data: state.product_quality_control_information?.data?.filter(
          (item: any) => item.id !== action.payload
        ),
        loading: false,
        error: null,
      };
    },
    removeProductById: (state, action) => {
      state.product_information = {
        data: state.product_information?.data?.filter(
          (item: any) => item.id !== action.payload
        ),
        loading: false,
        error: null,
      };
    },
    removeReporterById: (state, action) => {
      state.reporter_information = {
        data: state.reporter_information?.data?.filter(
          (item: any) => item.id !== action.payload
        ),
        loading: false,
        error: null,
      };
    },
    removeStudyById: (state) => {
      state.study_information = {
        data: null,
        loading: false,
        error: null,
      };
    },
    removeAdditionalInfoById: (state) => {
      state.additional_info = {
        data: null,
        loading: false,
        error: null,
      };
    },
    removeProductDetailById: (state) => {
      state.products_details = {
        data: null,
        loading: false,
        error: null,
      };
    },

    removeMedicalHistoryById: (state, action) => {
      if (
        state.medical_history_information?.data &&
        Array.isArray(state.medical_history_information.data)
      ) {
        state.medical_history_information = {
          data: state.medical_history_information.data.filter(
            (item: any) => item.id !== action.payload
          ),
          loading: false,
          error: null,
        };
      }
    },

    removeLiteratureById: (state, action) => {
      if (
        state.literature_information?.data &&
        Array.isArray(state.literature_information.data)
      ) {
        state.literature_information = {
          data: state.literature_information.data.filter(
            (item: any) => item.id !== action.payload
          ),
          loading: false,
          error: null,
        };
      }
    },

    removePastDrugHistoryById: (state, action) => {
      if (
        state.past_drug_history_information?.data &&
        Array.isArray(state.past_drug_history_information.data)
      ) {
        state.past_drug_history_information = {
          data: state.past_drug_history_information.data.filter(
            (item: any) => item.id !== action.payload
          ),
          loading: false,
          error: null,
        };
      }
    },

    removeParentInformationById: (state, action) => {
      if (
        state.parent_information?.data &&
        Array.isArray(state.parent_information.data)
      ) {
        state.parent_information = {
          data: state.parent_information.data.filter(
            (item: any) => item.id !== action.payload
          ),
          loading: false,
          error: null,
        };
      }
    },

    removeParentMedicalHistoryById: (state, action) => {
      if (
        state.parent_medical_history?.data &&
        Array.isArray(state.parent_medical_history.data)
      ) {
        state.parent_medical_history = {
          data: state.parent_medical_history.data.filter(
            (item: any) => item.id !== action.payload
          ),
          loading: false,
          error: null,
        };
      }
    },

    removeParentPastDrugHistoryById: (state, action) => {
      if (
        state.parent_past_drug_history?.data &&
        Array.isArray(state.parent_past_drug_history.data)
      ) {
        state.parent_past_drug_history = {
          data: state.parent_past_drug_history.data.filter(
            (item: any) => item.id !== action.payload
          ),
          loading: false,
          error: null,
        };
      }
    },

    removeNeonateById: (state, action) => {
      if (state.neonate_data?.data && Array.isArray(state.neonate_data.data)) {
        state.neonate_data = {
          data: state.neonate_data.data.filter(
            (item: any) => item.id !== action.payload
          ),
          loading: false,
          error: null,
        };
      }
    },
    addDrugToProductEventRelationship: (state, action) => {
      const { caseId, drugName, events } = action.payload;
      if (!state.product_event_relationship?.data) {
        state.product_event_relationship = {
          data: {
            [caseId]: {
              [drugName]: events,
            },
          },
          loading: false,
          error: null,
        };
      } else {
        if (!state.product_event_relationship.data[caseId]) {
          state.product_event_relationship.data[caseId] = {};
        }

        state.product_event_relationship.data[caseId][drugName] = events;
      }
    },
  },
});

export default caseSlice.reducer;
export const {
  removeSubstanceById,
  removeProductById,
  removeProductPartById,
  removeProductIndicationById,
  removeQualityControlById,
  removeReporterById,
  removeStudyById,
  removeAdditionalInfoById,
  removeMedicalHistoryById,
  removeProductDetailById,
  removePastDrugHistoryById,
  removeParentInformationById,
  removeParentMedicalHistoryById,
  removeParentPastDrugHistoryById,
  removeLiteratureById,
  addDrugToProductEventRelationship,
  removeNeonateById,
  setCurrentStudyTabData,
} = caseSlice.actions;
